import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import type { SxProps, Theme } from "@mui/material";
import {
  Box,
  Button,
  DialogActions,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { linearProgressClasses } from "@mui/material/LinearProgress";
import { useCallback, useMemo, useState } from "react";
import type { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { ReportInfo, ReportUsage } from "../../../../../../api/types/svTypes.ts";
import useUserContext from "../../../../../../context/useUserContext";
import { getViewReportUrl } from "../utilities/editReportUrl";
import CancelPromptDialog from "./CancelPromptDialog";
import { CompanyMultipleReportsListItem } from "./CompanyMultipleReportsListItem";
import { ValidationInfoItem, ValidationItemContainer } from "./CompanyMultipleReportsListItemContainer";
import { CompanySingleReportListItem } from "./CompanySingleReportListItem";
import { InProgressIcon } from "./controls/InProgressIcon";
import { ReportInProgress } from "./controls/ReportInProgress";
import type { TabNames } from "./controls/ResultButtonGroup.tsx";
import ResultButtonGroup from "./controls/ResultButtonGroup.tsx";
import type { CopyCompany, CopyCompanyReport } from "./hooks/CopyCompany";
import type { SelectedCompany } from "./hooks/SelectedCompany";
import useReportCopying from "./hooks/useReportCopying";

interface Props {
  reports: ReportInfo[];
  selectedCompanies: SelectedCompany[];
  copyConditionValues: boolean;
  copyAccess: ReportAccessType;
  inheritFromParent: boolean;
  override: boolean;
  groupsCreating: boolean;
  reportUsage: ReportUsage;
  onClose: (success?: boolean) => void;
}
export function CopyToDialogCopying({
  reports,
  selectedCompanies,
  copyConditionValues,
  copyAccess,
  inheritFromParent,
  override,
  groupsCreating,
  reportUsage,
  onClose,
}: Props) {
  const { clients } = useUserContext();
  const { copying, retryCopy, cancel } = useReportCopying(
    selectedCompanies,
    copyConditionValues,
    override,
    copyAccess,
    inheritFromParent,
    groupsCreating
  );
  const [selectedTab, setSelectedTab] = useState<TabNames>("all");
  const [showPrompt, setShowPrompt] = useState(false);

  const allCopyingReports = useMemo(() => copying.flatMap((c) => c.reports), [copying]);
  const isMultipleReports = useMemo(() => reports.length > 1, [reports]);

  const companyList = useMemo(() => {
    return clients
      .filter((c) => c.type === "FundManager")
      .map((c): ListItemData => ({
        value: c.clientCode,
        label: c.title,
        iconSrc: c.branding.logoMarkUrl,
      }));
  }, [clients]);

  const copiedToAllCompanies = useMemo(() => allCopyingReports.every((r) => r.copied || r.error), [allCopyingReports]);
  const copiedWithErrors = useMemo(() => allCopyingReports.some((r) => r.error), [allCopyingReports]);
  const copiedCount = useMemo(() => allCopyingReports.filter((r) => r.copied).length, [allCopyingReports]);
  const progressValue = useMemo(() => {
    const value = (copiedCount / allCopyingReports.length) * 100;
    return Number.isNaN(value) ? 0 : value;
  }, [allCopyingReports.length, copiedCount]);

  const filteredCompanyList = useMemo(() => {
    switch (selectedTab) {
      case "all":
        return copying;
      case "copied":
        return copying
          .filter((c) => c.reports.some((r) => r.copied))
          .map((c) => ({ ...c, reports: c.reports.filter((r) => r.copied) }));
      case "failed":
        return copying
          .filter((c) => c.reports.some((r) => r.error))
          .map((c) => ({ ...c, reports: c.reports.filter((r) => r.error) }));
    }
  }, [copying, selectedTab]);

  const total = useMemo(() => copying.length, [copying]);
  const copied = useMemo(() => copying.filter((r) => r.reports.some((r) => r.copied)).length, [copying]);
  const failed = useMemo(() => copying.filter((r) => r.reports.some((r) => r.error)).length, [copying]);

  const handleClosing = useCallback(() => {
    if (copiedToAllCompanies) {
      onClose(copiedToAllCompanies);
    } else {
      setShowPrompt(true);
    }
  }, [copiedToAllCompanies, onClose]);

  if (groupsCreating) {
    return <InlineLoader text="Preparing reports for copying..." />;
  }

  return (
    <>
      <DialogHeader text={`Copy Report${reports.length > 1 ? "s" : ""}`} onClose={handleClosing} />
      <Divider />
      <Stack sx={{ flex: 1, width: "100%" }}>
        <Grid container sx={{ flex: 1, justifyContent: "center", p: 2, width: "100%" }}>
          <Grid container sx={{ gap: 1, width: "55%" }}>
            <Stack sx={{ width: "100%" }}>
              <Typography color="secondary">Do not close this window until the copying process is complete</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", pt: 0.5 }}>
                <BorderLinearProgress variant="determinate" value={progressValue} sx={{ flex: 1 }} />
                <Typography color="secondary">
                  {copiedCount}/{allCopyingReports.length}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ gap: 1, height: 20 }}>
                {copiedToAllCompanies && (
                  <Typography color="secondary">
                    {copiedWithErrors ? "Completion with errors occurred" : "Completed!"}
                  </Typography>
                )}
              </Stack>
              <Box sx={{ pt: 2.5, width: "50%" }}>
                <ResultButtonGroup
                  copied={copied}
                  failed={failed}
                  total={total}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
              </Box>
              <ScrollableFlexContainer>
                {!isMultipleReports && (
                  <SingleReportCopying
                    companies={filteredCompanyList}
                    companyList={companyList}
                    reportUsage={reportUsage}
                    retryCopy={retryCopy}
                  />
                )}
                {isMultipleReports && (
                  <MultipleReportsCopying
                    companies={filteredCompanyList}
                    companyList={companyList}
                    retryCopy={retryCopy}
                    reportUsage={reportUsage}
                  />
                )}
              </ScrollableFlexContainer>
            </Stack>
          </Grid>
        </Grid>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="contained" onClick={handleClosing}>
            Close
          </Button>
        </DialogActions>
      </Stack>
      {showPrompt && (
        <CancelPromptDialog
          reports={reports}
          onOkay={() => {
            cancel();
            onClose();
          }}
          onCancel={() => setShowPrompt(false)}
        />
      )}
    </>
  );
}

interface SingleReportCopyingProps {
  companies: CopyCompany[];
  companyList: ListItemData[];
  reportUsage: ReportUsage;
  retryCopy: (report: CopyCompanyReport) => void;
}

function SingleReportCopying({ companies, companyList, reportUsage, retryCopy }: SingleReportCopyingProps) {
  return (
    <Stack sx={{ gap: 1, pt: 2, pb: 1 }}>
      {companies.map((company) => {
        const report = defined(company.reports[0]);
        const branding = companyList.find((c) => c.value === company.company.code);

        const getAction = () => {
          if (report.copied) {
            return <OpenReport companyReport={report} reportUsage={reportUsage} />;
          }
          if (report.error) {
            return <TryCopyAgain onRetry={() => retryCopy(report)} />;
          }
          return undefined;
        };

        return (
          <CompanySingleReportListItem
            key={company.company.code}
            company={company.company}
            companyBranding={branding}
            inProgress={report.copying || (!report.copied && !report.error)}
            Action={getAction()}
          >
            {report.copying && <ReportInProgress text="Copying the report to the company..." />}
            {!report.copying && !report.copied && !report.error && <CopyWaiting />}
            {report.error && <CopyError errorMessage={report.error} />}
            {report.copied && <Copied />}
          </CompanySingleReportListItem>
        );
      })}
    </Stack>
  );
}
function MultipleReportsCopying({ companies, companyList, retryCopy, reportUsage }: SingleReportCopyingProps) {
  const getAction = useCallback(
    (report: CopyCompanyReport) => {
      if (report.copied) {
        return <OpenReport sx={{ top: 15 }} companyReport={report} reportUsage={reportUsage} />;
      }
      if (report.error) {
        return <TryCopyAgain sx={{ top: 20 }} onRetry={() => retryCopy(report)} />;
      }
      return undefined;
    },
    [retryCopy, reportUsage]
  );

  return (
    <Stack sx={{ gap: 1, pt: 2, pb: 1 }}>
      {companies.map((company) => {
        const branding = companyList.find((c) => c.value === company.company.code);
        const isCopying = company.reports.some((r) => r.copying);

        return (
          <CompanyMultipleReportsListItem
            key={company.company.code}
            company={company.company}
            companyBranding={branding}
            Summary={isCopying ? <CopyingInfo company={company} /> : <CopiedInfo company={company} />}
          >
            {company.reports.map((report) => (
              <ValidationItemContainer
                key={report.report.reportId}
                caption={report.report.name}
                completed={report.copied}
                BottomAction={getAction(report)}
              >
                {report.copying && <ReportInProgress text="Copying the report to the company..." />}
                {!report.copying && !report.copied && !report.error && <CopyWaiting />}
                {report.error && <CopyError errorMessage={report.error} />}
                {report.copied && <Copied />}
              </ValidationItemContainer>
            ))}
          </CompanyMultipleReportsListItem>
        );
      })}
    </Stack>
  );
}

function CopyWaiting() {
  return <Typography color="secondary">Waiting to be copied...</Typography>;
}

function CopyError({ errorMessage }: { errorMessage: string }) {
  return <Typography color="error">{errorMessage || "An error occurred while copying the report"}</Typography>;
}

function Copied() {
  return (
    <Stack direction="row" sx={{ flexGrow: 1, alignItems: "center", gap: 0.5 }}>
      <CheckRoundedIcon color="success" />
      <Typography sx={{ color: "success.main" }}>The report has been successfully copied</Typography>
    </Stack>
  );
}
function OpenReport({
  companyReport,
  reportUsage,
  sx,
}: {
  companyReport: CopyCompanyReport;
  reportUsage: ReportUsage;
  sx?: SxProps<Theme>;
}) {
  const link = useMemo(() => {
    return getViewReportUrl(reportUsage)(companyReport.toCompanyCode, companyReport.newReportId || "");
  }, [companyReport.toCompanyCode, companyReport.newReportId, reportUsage]);

  return (
    <Box sx={{ position: "relative" }}>
      <Link
        href={link}
        target="_blank"
        sx={{
          whiteSpace: "nowrap",
          textDecoration: "none",
          position: "absolute",
          right: (theme) => theme.spacing(1),
          top: (theme) => theme.spacing(1.2),
          ...sx,
        }}
      >
        <Typography variant="subtitle2">Open Report</Typography>
      </Link>
    </Box>
  );
}

function TryCopyAgain({ onRetry, sx }: { onRetry?: () => void; sx?: SxProps<Theme> }) {
  return (
    <Box sx={{ position: "relative" }}>
      <Button
        color="error"
        sx={{
          whiteSpace: "nowrap",
          position: "absolute",
          right: (theme) => theme.spacing(1),
          top: (theme) => theme.spacing(0.5),
          ...sx,
        }}
        onClick={onRetry}
      >
        Try Again
      </Button>
    </Box>
  );
}

function CopyingInfo({ company }: { company: CopyCompany }) {
  const validatedCount = useMemo(() => {
    return company.reports.filter((r) => r.copied || r.error).length;
  }, [company.reports]);

  return (
    <Stack direction={"row"} spacing={1}>
      <Typography color={"secondary"}>
        {validatedCount}/{company.reports.length}
      </Typography>
      <InProgressIcon />
    </Stack>
  );
}

function CopiedInfo({ company }: { company: CopyCompany }) {
  const failedCount = useMemo(() => {
    return company.reports.filter((r) => r.error).length;
  }, [company.reports]);

  const successCount = useMemo(() => {
    return company.reports.filter((r) => r.copied && !r.error).length;
  }, [company.reports]);

  return (
    <Stack direction="row" spacing={1.5}>
      <ValidationInfoItem count={failedCount} Icon={ErrorRoundedIcon} color="error" />
      <ValidationInfoItem count={successCount} Icon={CheckRoundedIcon} color="success" />
    </Stack>
  );
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 4,
  },
}));
